import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Target, Heart, ArrowRight, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="About CTC Waterworks"
        description="Founded on decades of agricultural expertise, CTC Waterworks designs and manufactures proven solutions that help growers protect their crops and extend shelf life."
        canonicalPath="/about"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-ctc-blue/10 text-ctc-blue px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users size={16} />
              <span>Our Story</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About CTC Waterworks
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Founded on decades of agricultural and industrial expertise, we design and manufacture 
              proven solutions that help growers, packers, and processors protect their crops, 
              extend shelf life, and get the most from every drop of water.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-8 border border-ctc-blue/20">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Founded by Bob Bader in 1985 & acquired by Brandon Haley in 2016, this family-owned company has earned a reputation for building reliable, 
                practical systems — from sulfur burners that improve irrigation water quality to 
                innovative hydrocoolers that preserve fruit freshness from orchard to market.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, we proudly serve farms and facilities of all sizes across the Pacific Northwest 
                and beyond, with portable and stationary systems trusted by leading cherry growers, 
                vineyards, and diverse specialty crop producers.
              </p>
            </div>
          </motion.div>

          {/* Core Values */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                <Target className="text-white" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: Award,
                  title: 'Innovation & Excellence',
                  description: 'Continuously improving our designs and manufacturing processes to deliver the most effective agricultural water solutions.'
                },
                {
                  icon: Users,
                  title: 'Customer Partnership',
                  description: 'Building long-term relationships with growers by understanding their unique challenges and providing tailored solutions.'
                },
                {
                  icon: Target,
                  title: 'Proven Results',
                  description: 'Every system we build is designed to deliver measurable improvements in crop quality, yield, and operational efficiency.'
                },
                {
                  icon: Heart,
                  title: 'Agricultural Heritage',
                  description: 'Deep respect for farming traditions combined with modern engineering to support sustainable agriculture.'
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg border border-gray-200"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center mb-4">
                    <value.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Team Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              A Team Dedicated to Your Success
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Brandon Haley */}
              <Link to="/team/brandon">
                <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-8 border border-ctc-blue/20 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                      <img
                        src="/Brandon Haley.png"
                        alt="Brandon Haley"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Brandon Haley</h3>
                    <p className="text-ctc-blue font-semibold text-lg mb-4">CEO - Owner</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                    Brandon leads CTC Waterworks with decades of hands-on experience in manufacturing. As owner of the company, he focuses on building reliable systems, fostering strong customer relationships, partnerships, and helping customers see better profits through delivering better water solutions.
                  </p>

                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-ctc-blue font-semibold">
                      <span>View Contact Page</span>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Andre Savala */}
              <Link to="/team/andre">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                      <img
                        src="/Andre Savala.png"
                        alt="Andre Savala"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Andre Savala</h3>
                    <p className="text-green-700 font-semibold text-lg mb-4">Sales & Customer Support</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                    Andre is passionate about helping customers find solutions that fit their unique operations. With extensive experience working with growers, he plays a key role in supporting sulfur burner projects, hydrocooler installations, and day-to-day customer conversations across the agricultural equipment industry.
                  </p>

                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-green-600 font-semibold">
                      <span>View Contact Page</span>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Jordan Sloan */}
              <Link to="/team/jordan">
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-200 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col">
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                      <img
                        src="/Jordan Sloan.png"
                        alt="Jordan Sloan"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Jordan Sloan</h3>
                    <p className="text-pink-600 font-semibold text-lg mb-4">Customer Experience & Outreach</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                    Jordan plays a key role in connecting CTC Waterworks with growers, packers, and operators in the field. She focuses on listening to customers, understanding their challenges, and turning their feedback into clear, helpful communication. Through thoughtful outreach and content, Jordan helps make every interaction with CTC a positive one.
                  </p>

                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 text-pink-600 font-semibold">
                      <span>View Contact Page</span>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Our Commitment */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-ctc-blue via-ctc-blue-dark to-black rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6 text-center">Our Commitment</h2>
              <p className="text-xl leading-relaxed text-center mb-6 opacity-90">
                At CTC Waterworks, we believe better water means better crops — and better crops mean 
                stronger farms, healthier communities, and better returns for growers and packers alike.
              </p>
              <p className="text-lg leading-relaxed text-center opacity-80">
                We back every product with personalized service, deep expertise, and a commitment to 
                continuous improvement.
              </p>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Let's Talk</h2>
              <p className="text-lg text-gray-700 text-center mb-8">
                Whether you're looking to improve water quality, reduce soil pH drift, or extend the 
                shelf life of your harvest, we're here to help.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Phone className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Call Us</h3>
                  <p className="text-gray-600">(509) 901-7476</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Mail className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email Us</h3>
                  <p className="text-gray-600">Sales@CTCWaterworks.com</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center mx-auto mb-3">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Visit Us</h3>
                  <p className="text-gray-600">460 McLaughlin Rd<br />Yakima, WA 98908</p>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <p className="text-gray-600">
                  Visit our website, connect on LinkedIn, or contact us today to see how 
                  CTC Waterworks can help your operation thrive.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;